import {CommonModule} from '@angular/common';
import {Component} from '@angular/core';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ActivatedRoute, Router} from '@angular/router';
import {ActivityGranule, LessonGranuleEntity, NavigateToLessonOptions} from '@modules/activities/core/models';
import {CardComponent} from '@modules/activities/core/shared-components/card/card.component';
import {LessonsCardService} from '@modules/activities/core/lessons/services/lessons-card.service';
import {AuthorizationService} from '@modules/authorization';
import {ContestLessonsComponentContract, ContestPresentationModule, ContestService} from '@modules/contest';
import {Contest} from '@modules/contest/core/model/contest';
import {SyncRules} from '@modules/contest/core/model/rules';
import {ActivitiesService} from '@modules/contest/core/services/activities.service';
import {AssignationService} from '@modules/contest/core/services/assignation.service';
import {DataCardInterface} from 'fuse-core/components/card/data-card.interface';
import {DataCollection, DataEntity} from 'octopus-connect';
import {combineLatest, Observable, of, ReplaySubject} from 'rxjs';
import {map, mergeMap, switchMap, takeUntil, tap} from 'rxjs/operators';
import {DatacardService} from 'shared/datacard.service';
import {AutoUnsubscribeTakeUntilClass} from 'shared/models';
import {SharedModule} from 'shared/shared.module';
import {brand} from '../../../../../../src/app/settings/mathia';

@Component({
    selector: 'app-mathia-contest-lessons',
    templateUrl: './mathia-contest-lessons.component.html',
    styleUrls: ['./mathia-contest-lessons.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        SharedModule,
        CardComponent,
        ContestPresentationModule,
        MatProgressSpinnerModule,
    ],
})
export class MathiaContestLessonsComponent extends AutoUnsubscribeTakeUntilClass implements ContestLessonsComponentContract {
    public contest: Contest;
    public loading = true;
    public canCompete = false;
    public dataCards: DataCardInterface<LessonGranuleEntity>[] = [];
    public selectedLesson: LessonGranuleEntity;
    public subLessons: LessonGranuleEntity[] = [];
    public activities: Record<string, ActivityGranule[]> = {};

    constructor(
        private contestService: ContestService,
        private activitiesService: ActivitiesService,
        private assignationService: AssignationService,
        private authorization: AuthorizationService,
        private datacardService: DatacardService,
        private lessonsCardService: LessonsCardService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();

        this.route.parent.params.pipe(
            map(params => params['id']),
            switchMap(contestId => this.contestService.getContestById(contestId)),
            tap((contest: Contest) => {
                this.contest = contest;
                this.canCompete = this.authorization.currentUserCan(SyncRules.CompeteInContest);
            }),
            switchMap(() => this.route.queryParamMap),
            takeUntil(this.unsubscribeInTakeUntil),
        ).subscribe(queryParams => this.initialize(queryParams.get('lessonId')));
    }

    private initialize(selectedLessonId: string): void {
        this.loading = true;
        this.selectedLesson = null;
        this.subLessons = [];
        this.activities = {};

        if (!selectedLessonId) {
            this.loadContestLessons();
            return;
        }

        const lessonId = +selectedLessonId;
        if (!this.contest.lessons.some(contestLessonId => +contestLessonId === lessonId)) {
            this.backToLessons();
            return;
        }

        this.loadSelectedLesson(lessonId);
    }

    private loadContestLessons(): void {
        const lessonIds = this.contest?.lessons ?? [];
        const lessons$ = lessonIds.length
            ? combineLatest(lessonIds.map(lessonId => this.activitiesService.loadLesson$(lessonId)))
            : of([] as LessonGranuleEntity[]);

        lessons$.pipe(takeUntil(this.unsubscribeInTakeUntil)).subscribe({
            next: lessons => {
                this.dataCards = this.createDataCards(lessons);
                this.loading = false;
            },
            error: () => {
                this.dataCards = [];
                this.loading = false;
            },
        });
    }

    private createDataCards(lessons: LessonGranuleEntity[]): DataCardInterface<LessonGranuleEntity>[] {
        const callbacks: Partial<DataCardInterface<LessonGranuleEntity>> = {
            ...this.lessonsCardService.getCallbacks(),
            displayLesson: () => true,
            openLessonPage: lesson => this.selectLesson(lesson),
            play: lesson => {
                if (typeof lesson !== 'number' && typeof lesson !== 'string') {
                    this.selectLesson(lesson);
                }
            },
        };

        return this.datacardService.processResources(lessons, callbacks, 'currentUser');
    }

    private loadSelectedLesson(lessonId: number): void {
        this.loadAssignment(lessonId).pipe(
            mergeMap(() => this.activitiesService.loadLesson$(lessonId)),
            tap(lesson => this.selectedLesson = lesson),
            mergeMap(lesson => this.loadSubLessons(lesson)),
            takeUntil(this.unsubscribeInTakeUntil),
        ).subscribe({
            next: subLessons => {
                this.subLessons = subLessons;
                this.loading = false;
            },
            error: () => {
                this.subLessons = [];
                this.loading = false;
            },
        });
    }

    private loadAssignment(lessonId: number): Observable<DataEntity> {
        if (!this.canCompete) {
            return of(null);
        }

        return this.assignationService.getAssignmentsByLessonId(lessonId).pipe(
            map((collection: DataCollection) => collection.entities.filter((assignment: DataEntity) => {
                return +assignment.get('assignated_user').uid === this.assignationService.currentUserId;
            })),
            mergeMap(assignments => assignments.length
                ? of(assignments[0])
                : this.assignationService.createAssignment$(lessonId)),
            tap(assignment => {
                if (assignment) {
                    this.assignationService.setCurrentAssignment(assignment);
                }
            }),
        );
    }

    private loadSubLessons(lesson: LessonGranuleEntity): Observable<LessonGranuleEntity[]> {
        return this.loadChildren(lesson).pipe(
            map(children => children.filter(child => child.get('format')?.label === 'lesson') as LessonGranuleEntity[]),
            mergeMap(subLessons => {
                if (!subLessons.length) {
                    return of(subLessons);
                }

                return combineLatest(subLessons.map(subLesson => this.loadActivities(subLesson))).pipe(
                    tap(activitiesBySubLesson => {
                        activitiesBySubLesson.forEach((activities, index) => {
                            this.activities[subLessons[index].id] = activities;
                        });
                    }),
                    map(() => subLessons),
                );
            }),
        );
    }

    private loadActivities(subLesson: LessonGranuleEntity): Observable<ActivityGranule[]> {
        return this.loadChildren(subLesson).pipe(
            map(children => children.filter(child => child.get('format')?.label !== 'lesson') as ActivityGranule[]),
        );
    }

    private loadChildren(lesson: LessonGranuleEntity): Observable<DataEntity[]> {
        const callbackActivities$ = new ReplaySubject<Observable<DataEntity[]>>(1);
        this.activitiesService.loadActivities(lesson, callbackActivities$);

        return callbackActivities$.pipe(
            mergeMap(activities$ => activities$),
            takeUntil(this.unsubscribeInTakeUntil),
        );
    }

    private selectLesson(lesson: LessonGranuleEntity): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {lessonId: lesson.id},
            queryParamsHandling: 'merge',
        });
    }

    public backToLessons(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {lessonId: null},
            queryParamsHandling: 'merge',
        });
    }

    public hasActivities(subLesson: LessonGranuleEntity): boolean {
        return !!this.activities[subLesson.id]?.length;
    }

    public getSubLessonTypeImage(subLesson: LessonGranuleEntity): string {
        const lessonType = subLesson.get('metadatas')?.lessonType;
        if (Array.isArray(lessonType) && lessonType.length && lessonType[0]['image']) {
            return lessonType[0]['image'];
        }
        return '/assets/' + brand + '/images/thumbs/contest.png';
    }

    public playEvent(data: {subLesson: LessonGranuleEntity, i: number}): void {
        const options: NavigateToLessonOptions = {
            isActivitiesListMustBeDisplayed: false,
            startOnStepIndex: 0,
            exitLessonUrl: this.router.url,
        };

        this.activitiesService.navigateToLesson(data.subLesson, options, !this.canCompete);
    }
}
